import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
  getBatches,
  searchBatches,
  showDeleteBatchModal,
  hideDeleteBatchModal,
  deleteBatch,
  completeBatch
} from 'actions';

import Batches from 'components/Batches';
import ConfirmModal from 'components/Modals/ConfirmModal';
import DeleteBatchModal from 'components/Modals/DeleteBatchModal';
import withContainerErrorBoundary from 'components/ErrorBoundaries/ContainerErrorBoundary/withContainerErrorBoundary';

const WAIT_INTERVAL = 1000;
const ENTER_KEY = 13;

class BatchesContainer extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    history: PropTypes.shape({}).isRequired,
    isAuthenticated: PropTypes.bool.isRequired,
    settings: PropTypes.shape({}),
    permissions: PropTypes.arrayOf(PropTypes.string),
    isGettingBatches: PropTypes.bool.isRequired,
    batches: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      dateUploaded: PropTypes.string,
      folder: PropTypes.string,
      legalEntity: PropTypes.string,
      businessDivision: PropTypes.string,
      region: PropTypes.string,
      statusString: PropTypes.string,
      numberOfPacks: PropTypes.number,
      numberOfForms: PropTypes.number
    })),
    batchesCount: PropTypes.number,
    batchesPageNumber: PropTypes.number,
    batchesPageSize: PropTypes.number,
    isDeleteBatchModalShown: PropTypes.bool,
    batchToDelete: PropTypes.shape(),
    isDeletingBatch: PropTypes.bool,
    deleteBatchError: PropTypes.string,
    location: PropTypes.shape({
      key: PropTypes.string
    }).isRequired,
    isCompletingBatch: PropTypes.bool
  };

  static defaultProps = {
    settings: null,
    permissions: null,
    batches: null,
    batchesPageNumber: 1,
    batchesPageSize: 50,
    batchesCount: 0,
    isDeleteBatchModalShown: false,
    batchToDelete: null,
    isDeletingBatch: false,
    deleteBatchError: false,
    isCompletingBatch: false
  };

  constructor(props) {
    super(props);
    this.state = {
      searchQuery: null,
      selectionEnabled: false,
      currentPageNumber: props.batchesPageNumber,
      currentPageSize: props.batchesPageSize,
      isConfirmCompleteBatchModalShown: false,
      confirmBatchItem: null
    };
  }

  /* eslint-disable-next-line camelcase */
  UNSAFE_componentWillMount() {
    const {
      isAuthenticated,
      batchesPageNumber,
      batchesPageSize
    } = this.props;
    if (isAuthenticated) {
      this.fetchBatches(batchesPageNumber, batchesPageSize);
    }
  }

  shouldComponentUpdate({ location: { key: nextKey } }) {
    const {
      location: { key },
      batchesPageNumber,
      batchesPageSize,
    } = this.props;
    const updateComponent = key !== nextKey;
    if (updateComponent) {
      this.fetchBatches(batchesPageNumber, batchesPageSize);
    }
    return !updateComponent;
  }

  componentDidUpdate() {
    if (this.batchesGrid) {
      const { selectionEnabled } = this.state;
      this.batchesGrid.setSelectionVisibility(selectionEnabled);
    }
  }

  getBatchRefreshAction(pageNumber, pageSize) {
    const { searchQuery } = this.state;
    let sortModel;
    let filters;
    let actionMethod;

    if (this.batchesGrid) {
      sortModel = this.batchesGrid.getSortModel();
      filters = this.batchesGrid.getFilterModel();
    }

    const sortBy = (sortModel && sortModel[0]) ? sortModel[0].colId : '';
    const sortDirection = (sortModel && sortModel[0]) ? sortModel[0].sort : '';

    if (pageNumber === 'undefined' || !pageNumber) {
      pageNumber = 1;
    }
    if (pageSize === 'undefined' || !pageSize) {
      pageSize = 50;
    }

    let actionParameters = [pageNumber, pageSize, sortBy, sortDirection, filters];
    if (!searchQuery && !sortModel && !filters) {
      actionMethod = getBatches;
    } else {
      actionMethod = searchBatches;
      actionParameters = [...actionParameters, searchQuery];
    }

    return [actionMethod, actionParameters];
  }

  triggerSearch = () => {
    const { currentPageSize } = this.state;
    let { currentPageNumber } = this.state;
    currentPageNumber = 1;
    this.setState({ currentPageNumber });
    this.batchesGrid.resetPageNumber(currentPageNumber);

    this.fetchBatches(currentPageNumber, currentPageSize);
  };

  deleteBatch() {
    const {
      dispatch,
      batchToDelete: {
        id,
        batchId
      }
    } = this.props;
    const {
      currentPageNumber,
      currentPageSize
    } = this.state;

    const refreshAction = this.getBatchRefreshAction(currentPageNumber, currentPageSize);
    dispatch(deleteBatch(id, batchId, refreshAction));
  }

  fetchBatches(pageNumber, pageSize) {
    const { dispatch } = this.props;
    const refreshAction = this.getBatchRefreshAction(pageNumber, pageSize);
    if (refreshAction && refreshAction.length === 2) {
      dispatch(refreshAction[0](...refreshAction[1]));
    }
  }

  searchChanged(event) {
    clearTimeout(this.timer);
    const searchQuery = event.target.value;

    if (searchQuery.length > 0 && searchQuery.length < 2) {
      return;
    }

    this.setState({
      searchQuery
    });

    this.timer = setTimeout(this.triggerSearch, WAIT_INTERVAL);
  }

  searchInGrid(event) {
    clearTimeout(this.timer);
    const searchQuery = event.target.value;

    if (searchQuery.length > 0 && searchQuery.length < 2) {
      return;
    }

    this.setState({
      searchQuery
    });

    if (event.keyCode === ENTER_KEY) {
      this.triggerSearch();
    } else {
      this.timer = setTimeout(this.triggerSearch, WAIT_INTERVAL);
    }
  }

  changePage(paging) {
    this.setState({
      currentPageNumber: paging.number,
      currentPageSize: paging.size
    });
    this.fetchBatches(paging.number, paging.size);
    return true;
  }

  openConfirmCompleteBatchModal(confirmBatchItem) {
    this.setState({ isConfirmCompleteBatchModalShown: true, confirmBatchItem });
  }

  closeConfirmCompleteBatchModel() {
    this.setState({ isConfirmCompleteBatchModalShown: false });
  }

  completeBatch() {
    const { dispatch } = this.props;
    const { confirmBatchItem, currentPageNumber, currentPageSize } = this.state;

    let sortModel;
    let filters;
    if (this.batchesGrid) {
      sortModel = this.batchesGrid.getSortModel();
      filters = this.batchesGrid.getFilterModel();
    }
    const sortBy = (sortModel && sortModel[0]) ? sortModel[0].colId : '';
    const sortDirection = (sortModel && sortModel[0]) ? sortModel[0].sort : '';

    dispatch(completeBatch(confirmBatchItem.id, getBatches, [currentPageNumber, currentPageSize, sortBy, sortDirection, filters]));
    this.setState({ isConfirmCompleteBatchModalShown: false });
  }

  openDeleteBatchModal(item) {
    const { dispatch } = this.props;
    dispatch(showDeleteBatchModal(item));
  }

  closeDeleteBatchModal() {
    const { dispatch } = this.props;
    dispatch(hideDeleteBatchModal());
  }

  render() {
    const {
      settings,
      permissions,
      isGettingBatches,
      batches,
      batchesCount,
      isDeleteBatchModalShown,
      isDeletingBatch,
      isCompletingBatch,
      deleteBatchError,
      batchesPageNumber,
      batchesPageSize
    } = this.props;

    const {
      isConfirmCompleteBatchModalShown,
      confirmBatchItem
    } = this.state;

    return (
      <div className="container home-page-panel">
        <div className="panel col-12 batches-panel">
          <div className="panel-header">
            <div className="panel-title">Uploaded Batches</div>
            <div>
              <div className="tool-panel">
                <div className="controls-group">
                  <div className="input-group">
                    <input
                      type="search"
                      className="form-input"
                      placeholder="Search Batches..."
                      onKeyUp={ e => this.searchInGrid(e) }
                      onChange={ e => this.searchChanged(e) }
                    />
                    <span className="input-group-addon">
                      <span className="icon icon-search" />
                    </span>
                    <button id="refresh-button" type="button" className="btn ml" onClick={ () => this.fetchBatches(batchesPageNumber, batchesPageSize) }>
                      <span className="icon icon-refresh" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="panel-body">
            <Batches
              settings={ settings }
              permissions={ permissions }
              ref={ (ref) => { this.batchesGrid = ref; } }
              isLoading={ isGettingBatches }
              rowData={ batches }
              totalCount={ batchesCount }
              deleteBatchCallback={ batch => this.openDeleteBatchModal(batch) }
              changePageCallback={ batch => this.changePage(batch) }
              editLegalEntityCallback={ batch => this.openEditLegalEntityModal(batch) }
              completeBatchCallback={ batch => this.openConfirmCompleteBatchModal(batch) }
            />
          </div>
        </div>
        { isDeleteBatchModalShown && <DeleteBatchModal
          isShown={ isDeleteBatchModalShown }
          isDeletingBatch={ isDeletingBatch }
          onConfirmDelete={ () => this.deleteBatch() }
          onCancel={ () => this.closeDeleteBatchModal() }
          deleteBatchError={ deleteBatchError }
        /> }

        { isConfirmCompleteBatchModalShown && <ConfirmModal
          key="completeBatch"
          isShown={ isConfirmCompleteBatchModalShown }
          isPerformingAction={ isCompletingBatch }
          onConfirm={ () => this.completeBatch() }
          onCancel={ () => this.closeConfirmCompleteBatchModel() }
          title="Confirm Complete Batch"
          message={ `Are you sure you want to complete the batch ${ confirmBatchItem.batchId }?` }
        /> }
      </div>
    );
  }
}

function mapStateToProps(state) {
  if (!state.auth) {
    return {};
  }

  const {
    auth: {
      isAuthenticated,
      settings,
      permissions
    },
    batches: {
      isGettingBatches,
      batches,
      batchesCount,
      isEditLegalEntityModalShown,
      isEditingLegalEntity,
      batchToEdit,
      editLegalEntityError,
      batchToDelete,
      isDeleteBatchModalShown,
      isDeletingBatch,
      deleteBatchError,
      isCompletingBatch
    }
  } = state;

  return {
    isAuthenticated,
    settings,
    permissions,
    isGettingBatches,
    batches,
    batchesCount,
    isEditLegalEntityModalShown,
    isEditingLegalEntity,
    batchToEdit,
    editLegalEntityError,
    isDeleteBatchModalShown,
    batchToDelete,
    isDeletingBatch,
    deleteBatchError,
    isCompletingBatch
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(withContainerErrorBoundary(BatchesContainer, 'BatchesContainer')));
